import { Component, OnDestroy, inject } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DialogService as PrimeNgDialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import jwtDecode from 'jwt-decode';
import { FASDynamicTableColumnConfig } from 'fas-services';
import { IAccessTicketVm } from '../../../../api/access-ticket-vm.model';
import { InstructionPdfService } from '../../../shared/services/instruction-pdf.service';
import {
  InstructionOptionsComponent,
  InstructionOptionsResult,
} from '../instruction-options/instruction-options.component';
import { BookAccessTicketsService } from '../../../../api/api-services/book-access-tickets.service';
import { IBookAccessTicketVm } from '../../../../api/book-access-ticket-vm.model';
import { IPaginatedResultBookAccessTicketVm } from '../../../../api/paginated-result-book-access-ticket-vm.model';
import { SortOrder } from '../../../../api/sort-order.model';
import { centerDynamicModalConfig } from '../../../shared/helpers/modal.config';
import { QrcodeDetailsComponent } from '../qrcode-details/qrcode-details.component';

@Component({
  selector: 'app-book-access-ticket-search',
  templateUrl: './book-access-ticket-search.component.html',
  styleUrl: './book-access-ticket-search.component.scss',
})
export class BookAccessTicketSearchComponent implements OnDestroy {
  private _bookAccessTicketsService = inject(BookAccessTicketsService);
  private _primengDialogService = inject(PrimeNgDialogService);
  private _instructionPdfService = inject(InstructionPdfService);
  private _messageService = inject(MessageService);

  public searchPhrase = '';
  /** Fraza użyta przy ostatnim wyszukaniu — tabela odpytuje BE dopiero po kliknięciu "Szukaj". */
  public appliedPhrase = '';
  public searchTrigger = new Subject<void>();
  public generatingInstructionCode: string | null = null;
  /** Wyszukiwarki używa też WorkerUserGenerator, a strona użytkowników jest tylko dla operatora. */
  public readonly canOpenAccounts = BookAccessTicketSearchComponent.hasRole('PlatformOperator');

  private ref: DynamicDialogRef | undefined;

  /** Rola z tokenu, tak jak czyta ją fas-admin (claim `Roles`). */
  private static hasRole(role: string): boolean {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return false;
    }
    try {
      const roles = jwtDecode<{ Roles?: string | string[] }>(token).Roles;
      return Array.isArray(roles) ? roles.includes(role) : roles === role;
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    if (this.ref) {
      this.ref.close();
      this.ref = undefined;
    }
  }

  public ticketColumns: FASDynamicTableColumnConfig<IBookAccessTicketVm>[] = [
    { field: 'code', columnHeader: 'Kod' },
    { field: 'isUsed', columnHeader: 'Wykorzystany', columnTemplateKey: 'isUsedTemplate' },
    { field: 'usedByUserAccount', columnHeader: 'Nazwa użytkownika', columnTemplateKey: 'usedByTemplate' },
    {
      field: 'bookAccessPackageValidDays',
      columnHeader: 'Ważność (dni)',
      columnTemplateKey: 'validDaysTemplate',
    },
    { field: 'oskName', columnHeader: 'Przypisany OSK', columnTemplateKey: 'oskNameTemplate' },
    {
      columnHeader: 'Akcje',
      columnClass: '',
      actions: [
        {
          label: '',
          tooltip: 'Podgląd kodu QR',
          buttonClass: 'pi pi-eye cursor-pointer text-center appear transparent-button',
          callback: (data: IBookAccessTicketVm) => this.openQrCode(data),
        },
        {
          label: '',
          tooltip: 'Pobierz instrukcję dla tego kodu',
          buttonClass: 'pi pi-file-pdf cursor-pointer text-center appear transparent-button',
          callback: (data: IBookAccessTicketVm, rowIndex: number) =>
            this.downloadInstructionForTicket(data, rowIndex),
        },
      ],
    },
  ];

  fetchTickets = (params: {
    page: number;
    pageSize: number;
    sortField?: keyof IBookAccessTicketVm;
  }): Observable<{ data: IBookAccessTicketVm[]; total: number }> => {
    const { page, pageSize, sortField = '' } = params;
    // BE bez searchText zwraca pustą stronę — nie wysyłamy więc zapytania, dopóki nie ma frazy.
    if (!this.appliedPhrase) {
      return of({ data: [], total: 0 });
    }
    return this._bookAccessTicketsService
      .get(page, pageSize, sortField as string, SortOrder.Descending, this.appliedPhrase)
      .pipe(
        map((result: IPaginatedResultBookAccessTicketVm) => ({
          data: result.data ?? [],
          total: result.allElementsCount,
        }))
      );
  };

  search(): void {
    this.appliedPhrase = (this.searchPhrase ?? '').trim();
    this.searchTrigger.next();
  }

  clear(): void {
    this.searchPhrase = '';
    this.appliedPhrase = '';
    this.searchTrigger.next();
  }

  openQrCode(data: IBookAccessTicketVm): void {
    this.ref = this._primengDialogService.open(QrcodeDetailsComponent, {
      ...centerDynamicModalConfig,
      data: this.toAccessTicket(data),
      header: 'Podgląd kodu QR',
      showHeader: true,
      closable: true,
    });
  }

  /** Ten sam przepływ co w szczegółach paczki (/BookAccessDetails): wybór języka, potem pojedynczy PDF A5. */
  downloadInstructionForTicket(data: IBookAccessTicketVm, rowIndex: number): void {
    if (this.generatingInstructionCode) {
      return;
    }
    const optionsRef = this._primengDialogService.open(InstructionOptionsComponent, {
      ...centerDynamicModalConfig,
      width: '450px',
      breakpoints: { '450px': '100vw' },
      data: { mode: 'single' },
    });

    optionsRef.onClose.subscribe(async (result: InstructionOptionsResult | undefined) => {
      if (!result) {
        return;
      }
      this.generatingInstructionCode = data.code;
      try {
        await this._instructionPdfService.downloadOne(this.toAccessTicket(data), rowIndex + 1, result.language);
      } catch (err) {
        console.error('Błąd generowania instrukcji:', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się wygenerować instrukcji. Spróbuj ponownie później.',
        });
      } finally {
        this.generatingInstructionCode = null;
      }
    });
  }

  /** Wyszukiwarka operuje na BookAccessTicketVm, a podgląd QR i generator PDF przyjmują AccessTicketVm. */
  private toAccessTicket(data: IBookAccessTicketVm): IAccessTicketVm {
    return {
      guid: data.guid,
      code: data.code,
      isUsed: data.isUsed,
      usedDate: data.usedDate,
      usedByEmail: data.usedByUserAccount,
    };
  }
}
